import React, { useCallback } from 'react';
import type { CustomFilterProps, CustomFloatingFilterProps } from 'ag-grid-react';
import { useGridFilter } from 'ag-grid-react';
import StatusFilterDropdown from '@/components/UI/StatusFilterDropdown/StatusFilterDropdown';
import { MAIN_CR, STATUS, STATUS_FILTER_SEPARATOR, SUB_CR } from '@/helpers/constants';

type StatusFilterValue = { value: { key: string; type: string[] }[] } | null;

type StatusServerModel = { values: string[] } | null;

const toServerModel = (filter: StatusFilterValue): StatusServerModel => {
  if (!filter?.value?.length) return null;

  return {
    values: filter.value.flatMap((selected) => selected.type.map((type) => `${type}${STATUS_FILTER_SEPARATOR}${selected.key}`)),
  };
};

const fromServerModel = (model: StatusServerModel | null | undefined): StatusFilterValue => {
  if (!model?.values || !Array.isArray(model.values) || model.values.length === 0) return null;
  const byKey: Record<string, { key: string; type: string[] }> = {};
  model.values.forEach((item: string) => {
    const [type, ...rest] = item.split(STATUS_FILTER_SEPARATOR);
    const key = rest.join(STATUS_FILTER_SEPARATOR);

    if (!byKey[key]) {
      byKey[key] = { key, type: [] };
    }
    byKey[key].type.push(type);
  });

  return { value: Object.values(byKey) };
};

interface StatusFilterAgGridProps extends CustomFilterProps<Record<string, unknown>, unknown, StatusServerModel> {
  statuses: string[];
}

export const StatusFilterAgGrid: React.FC<StatusFilterAgGridProps> = (props) => {
  const { statuses, model, onModelChange, column } = props;
  const filter = fromServerModel(model);

  const doesFilterPass = useCallback((params: { data?: Record<string, unknown> }) => {
    const data = params?.data;
    const current = fromServerModel(model);
    if (!data || !current?.value?.length) return true;
    const rowStatus = data.statusCode as string | undefined;
    const isSubCR = Boolean(data.isSubCRChildren);
    const rowType = isSubCR ? SUB_CR : MAIN_CR;

    return current.value.some((selected) => selected.key === rowStatus && selected.type?.includes(rowType));
  }, [model]);

  useGridFilter({ doesFilterPass });

  const handleFilterChange = useCallback((newFilter: StatusFilterValue) => {
    onModelChange(toServerModel(newFilter));
  }, [onModelChange]);

  return (
    <StatusFilterDropdown
      id={`status-filter-${column?.getId() || STATUS}`}
      statuses={statuses}
      filter={filter}
      onFilter={handleFilterChange}
    />
  );
};

interface StatusFloatingFilterProps extends CustomFloatingFilterProps<CustomFilterProps, Record<string, unknown>, unknown, StatusServerModel> {
  statuses: string[];
}

export const StatusFloatingFilter: React.FC<StatusFloatingFilterProps> = (props) => {
  const { statuses, model, onModelChange, column } = props;
  const filter = fromServerModel(model);

  const handleFilterChange = useCallback((newFilter: StatusFilterValue) => {
    onModelChange(toServerModel(newFilter));
  }, [onModelChange]);

  return (
    <StatusFilterDropdown
      id={`status-floating-filter-${column?.getId() || STATUS}`}
      statuses={statuses}
      filter={filter}
      onFilter={handleFilterChange}
    />
  );
};
